import { useNavigate, useSearch } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AdminApi } from "@/api";
import { CONVERSATIONS_ROUTE } from "@/components/conversations/conversationsSearch";
import { rememberSearch } from "@/components/layout/lastSearch";
import {
  ALL_TEST_CASES_ROUTE,
  domainLabel,
  FEEDBACK_CATEGORIES,
  isProposedDomain,
  isRetiredCategory,
  isSort,
  isStateFilter,
  RUBRIC_DOMAINS,
  SORT_OPTIONS,
  STATE_FILTER_OPTIONS,
  UNCLASSIFIED_DOMAIN,
  type AllTestCasesSearch,
  type AllTestCasesSort,
} from "@/components/test-cases/allTestCasesSearch";
import { testCaseGate, traceEnvironment } from "@/components/test-cases/prodCase";
import { DROPPED_LABEL, droppedReasonText, isDropped } from "@/components/test-cases/dropState";
import {
  compareFeedbackCountDesc,
  isSourceDeleted,
  isSourceUnavailable,
  SOURCE_DELETED_LABEL,
  SOURCE_DELETED_TEXT,
  SOURCE_UNAVAILABLE_LABEL,
  sourceUnavailableText,
} from "@/components/test-cases/sourceStatus";
import { TestCaseDrawer } from "@/components/test-cases/TestCaseDrawer";
import {
  TEST_RESULT_DEFAULT_SEARCH,
  TEST_RESULT_ROUTE,
} from "@/components/test-result/testResultSearch";
import type { TestCaseSource } from "@/components/test-cases/testCasesSource";
import {
  Badge,
  badgeVariants,
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";
import type { FeedbackCategory, TestCaseDetail, TestCaseListItem } from "@/types";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 30;

function errorText(err: unknown) {
  return err instanceof Error ? err.message : String(err);
}

/**
 * All Test Cases — the first reader of `eval_test_case` (fe/TEST_CASES_ALL.md).
 *
 * Split master-detail, not a dialog: browsing TCs is a continuous action,
 * and the drawer keeps the list on the left so the next row can be opened
 * without closing anything. Open/closed lives in the URL's `?tc`.
 *
 * Everything the screen filters or sorts is URL state (`?q` · `?cats` ·
 * `?state` · `?sort` · `?tc`), validated into sane defaults by
 * `parseAllTestCasesSearch` — a pasted or hand-edited link lands on a
 * working screen, never on a thrown error.
 *
 * The rows are fetched once (the whole set, retired included) and
 * filtered/sorted here: the seed is a few dozen rows (BE §11.2), the count
 * line needs the unfiltered total, and the BE contract has no sort axis. If
 * the set outgrows that, the filters move server-side — the API client
 * already takes them.
 *
 * The one exception to "state lives in the URL" is the run selection (§4.2).
 * It exists to be handed to Batch Test and is spent the moment it is: a link
 * that re-created a half-made choice on someone else's screen would be a
 * draft nobody asked for.
 */
export function AllTestCasesPage({ api, reloadKey = 0 }: { api: AdminApi; reloadKey?: number }) {
  const search = useSearch({ from: ALL_TEST_CASES_ROUTE });
  const navigate = useNavigate();

  // The sidebar brings the tab back to this search (layout/lastSearch).
  useEffect(() => {
    rememberSearch(ALL_TEST_CASES_ROUTE, search);
  }, [search]);

  const source = useMemo<TestCaseSource>(
    () => ({
      list: ({ cursor, limit, activeOnly }) => api.listTestCases({ cursor, limit, activeOnly }),
      detail: (testCaseId) => api.getTestCase(testCaseId),
      setActive: (testCaseId, isActive) => api.patchTestCase(testCaseId, { is_active: isActive }),
      regenerateCriteria: (testCaseId, environment, body) =>
        api.regenerateTestCaseCriteria(testCaseId, environment, body),
      criteriaVersions: (testCaseId, environment) =>
        api.listTestCaseCriteriaVersions(testCaseId, environment),
    }),
    [api],
  );
  const originCandidates = useCallback(
    (testCaseId: string) => api.listCandidates({ test_case_id: testCaseId }).then((page) => page.items),
    [api],
  );

  // --- List data: cursor-paginated, accumulated -------------------------

  const [items, setItems] = useState<TestCaseListItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [loadErrors, setLoadErrors] = useState<Record<string, string> | null>(null);

  const loadFirst = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const page = await source.list({ cursor: null, limit: PAGE_SIZE, activeOnly: false });
      setItems(page.items ?? []);
      setLoadErrors(page.load_errors ?? null);
      setNextCursor(page.next_cursor ?? null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [source]);

  // `reloadKey` is the header's Reload.
  useEffect(() => {
    void loadFirst();
  }, [loadFirst, reloadKey]);

  const loadMore = useCallback(async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError("");
    try {
      const page = await source.list({ cursor: nextCursor, limit: PAGE_SIZE, activeOnly: false });
      setItems((prev) => [...prev, ...(page.items ?? [])]);
      if (page.load_errors) setLoadErrors(page.load_errors);
      setNextCursor(page.next_cursor ?? null);
    } catch (err) {
      // A failed page is no reason to forget what was already read: the
      // banner says what broke, the rows stay.
      setError(errorText(err));
    } finally {
      setLoadingMore(false);
    }
  }, [nextCursor, loadingMore, source]);

  // --- URL state changes --------------------------------------------------
  // Hub §4.3: filter/sort changes and opening the drawer are pushes (back
  // returns to the previous filter); closing is a replace, so the history
  // does not gain a "same list, drawer closed" entry in between.

  const updateSearch = useCallback(
    (patch: Partial<AllTestCasesSearch>, opts: { replace?: boolean } = {}) => {
      // The whole object rather than an updater: `search` here is already
      // the normalized shape, so spreading it keeps every field present and
      // avoids depending on what the router hands a reducer.
      navigate({
        to: ALL_TEST_CASES_ROUTE,
        search: { ...search, ...patch },
        replace: opts.replace,
      });
    },
    [navigate, search],
  );

  const toggleDrawer = (id: string) => {
    if (search.tc === id) updateSearch({ tc: null }, { replace: true });
    else updateSearch({ tc: id });
  };

  const toggleDom = (slug: string) => {
    const next = search.doms.includes(slug)
      ? search.doms.filter((dom) => dom !== slug)
      : [...search.doms, slug];
    // Picking a domain narrows what feedback chips exist, and a chip that is
    // no longer in range would filter to a quiet zero (§3.1). Drop those.
    const inRange = new Set(
      items
        .filter((item) => next.length === 0 || next.includes(domainOf(item)))
        .flatMap((item) => item.categories),
    );
    updateSearch({ doms: next, cats: search.cats.filter((cat) => inRange.has(cat)) });
  };

  const toggleCat = (slug: FeedbackCategory) => {
    const next = search.cats.includes(slug)
      ? search.cats.filter((cat) => cat !== slug)
      : [...search.cats, slug];
    updateSearch({ cats: next });
  };

  // The search box is a draft until Enter: committing per keystroke would
  // push a history entry per letter. Back/forward still sync the draft.
  const [qDraft, setQDraft] = useState(search.q);
  useEffect(() => {
    setQDraft(search.q);
  }, [search.q]);

  const commitQ = () => {
    const next = qDraft.trim();
    if (next !== search.q) updateSearch({ q: next });
  };

  const applyState = (value: unknown) => {
    if (isStateFilter(value)) updateSearch({ state: value });
  };

  const applySort = (value: unknown) => {
    if (isSort(value)) updateSearch({ sort: value });
  };

  // --- Run selection: component state, handed to Batch Test ---------------
  // This screen runs nothing (fe/TEST_CASES_ALL.md §4.2). It passes the ids
  // to a new Batch Test page as that page's draft; the count decides the
  // mode — one is a single page, two or more a multi page.

  const [picked, setPicked] = useState<string[]>([]);
  const [handing, setHanding] = useState(false);
  const pickedSet = useMemo(() => new Set(picked), [picked]);

  const togglePick = (id: string) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const startTest = async () => {
    if (picked.length === 0 || handing) return;
    // The page keeps the order it is given, so give it the one on screen
    // rather than the order the boxes happened to be ticked in.
    const rank = new Map(
      sortTestCases(items, search.sort).map((item, index) => [item.test_case_id, index]),
    );
    const ids = [...picked].sort(
      (a, b) => (rank.get(a) ?? rank.size) - (rank.get(b) ?? rank.size),
    );
    setHanding(true);
    setError("");
    try {
      const page = await api.createBatchPage({
        mode: ids.length === 1 ? "single" : "multi",
        test_case_ids: ids,
      });
      // Server first, then replace (hub §4.3). Not reset on success: the
      // button stays busy until the route changes, so a second click cannot
      // make a second page.
      navigate({ to: "/test-cases/batch/$pageId", params: { pageId: page.page_id }, replace: true });
    } catch (err) {
      setError(errorText(err));
      setHanding(false);
    }
  };

  /**
   * Adopt the PATCH response into the loaded page.
   *
   * The detail is a superset of the row, so it replaces it outright — no
   * field-by-field merge to fall out of date. Editing is not navigation, so
   * the URL does not change and nothing refetches: the row dims (or leaves,
   * if a filter excludes it now) exactly where it was.
   */
  const adoptUpdated = useCallback((updated: TestCaseDetail) => {
    setItems((prev) =>
      prev.map((item) =>
        item.test_case_id === updated.test_case_id ? updated : item,
      ),
    );
    // A retired TC cannot be locked into a page (BE refuses it), so it leaves
    // the selection along with its checkbox — the count says so. So does one
    // the fresh response no longer lets onto a page.
    if (!updated.is_active || !testCaseGate(updated, "add_to_batch").allowed) {
      setPicked((prev) => prev.filter((id) => id !== updated.test_case_id));
    }
  }, []);

  // The Matrix row for this TC — Test Result picks the newest page that
  // holds it (fe/TEST_CASES_ALL.md §6.6). A move, so a push.
  const openResult = (item: TestCaseDetail) => {
    navigate({
      to: TEST_RESULT_ROUTE,
      search: { ...TEST_RESULT_DEFAULT_SEARCH, tc: item.test_case_id },
    });
  };

  const openOriginal = (item: TestCaseDetail) => {
    // `source_environment` is a free string — a local run writes its own
    // name (BE §19.6) — but a trace can only be read from dev or prod. A
    // name that is neither is not guessed into dev; the drawer disables the
    // link for it.
    const env = traceEnvironment(item);
    if (!env) return;
    // Conversations reads the trace (hub §4.1). With all three params it
    // skips its search step and opens this answer's trace directly.
    navigate({
      to: CONVERSATIONS_ROUTE,
      search: {
        env,
        session: item.source_session_id,
        message: item.assistant_message_id,
        // No step of the trace picked — the link opens the answer, and the
        // reader chooses what to look into from there.
        node: null,
        // The session list's filters — nothing to go back to from a link.
        q: null,
        email: null,
      },
    });
  };

  // --- Derived: filter, sort, chips, count -------------------------------

  const visible = useMemo(
    () => sortTestCases(filterTestCases(items, search), search.sort),
    [items, search],
  );

  const filtersActive =
    search.q !== "" ||
    search.cats.length > 0 ||
    search.doms.length > 0 ||
    search.state !== "all";

  // "All" is what is listed and can run — never "everything matching": the
  // cursor pages mean the client does not know that set (Batch §2).
  // Only what a Batch Test page can hold: active, and allowed onto one.
  const pickable = useMemo(
    () => visible.filter((item) => item.is_active && testCaseGate(item, "add_to_batch").allowed),
    [visible],
  );
  const pickedOnScreen = pickable.filter((item) => pickedSet.has(item.test_case_id)).length;
  const offList = useMemo(() => {
    const shown = new Set(visible.map((item) => item.test_case_id));
    return picked.filter((id) => !shown.has(id)).length;
  }, [picked, visible]);

  const setPickable = (on: boolean) => {
    const ids = pickable.map((item) => item.test_case_id);
    setPicked((prev) =>
      on ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id)),
    );
  };

  const envCounts = useMemo(() => {
    let dev = 0;
    let prod = 0;
    for (const item of visible) {
      if (item.source_environment === "dev") dev += 1;
      else if (item.source_environment === "prod") prod += 1;
    }
    return { dev, prod };
  }, [visible]);

  /**
   * The domain tier: the five scored domains first, then the classifier's
   * proposals, then 미분류 (§3.2). Only what the loaded set actually holds,
   * each with its count — a 0-count chip is a control that leads nowhere.
   *
   * Counted over `items`, not `visible`: a chip whose count moved every time
   * another chip was picked could not be read as "how many of these exist".
   */
  const chipDoms = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) {
      const slug = domainOf(item);
      counts.set(slug, (counts.get(slug) ?? 0) + 1);
    }
    const proposed = [...counts.keys()].filter(isProposedDomain).sort();
    const order = [
      ...RUBRIC_DOMAINS.filter((slug) => counts.has(slug)),
      ...proposed,
      ...(counts.has(UNCLASSIFIED_DOMAIN) ? [UNCLASSIFIED_DOMAIN] : []),
    ];
    return order.map((slug) => ({ slug, count: counts.get(slug) ?? 0 }));
  }, [items]);

  // Chips for what actually exists in the loaded set, in the fixed
  // vocabulary order. A 0-count chip would be a control that leads nowhere.
  //
  // Scoped to the picked domains (§3.1): the question is "what went wrong in
  // THIS domain", and offering the rest leads to an empty list.
  const chipCats = useMemo(() => {
    const present = new Set<FeedbackCategory>();
    for (const item of items) {
      if (search.doms.length > 0 && !search.doms.includes(domainOf(item))) continue;
      for (const cat of item.categories) present.add(cat);
    }
    return FEEDBACK_CATEGORIES.filter((cat) => present.has(cat));
  }, [items, search.doms]);

  return (
    <div
      className={cn(
        "grid items-start gap-5",
        search.tc && "lg:grid-cols-[minmax(0,1fr)_400px]",
      )}
    >
      <div className="grid min-w-0 gap-3">
        {error && <Banner tone="error">{error}</Banner>}
        {/* The catalog reads dev and prod; a database it could not reach is
            a hole in the list, not an empty one — say which. */}
        {loadErrors &&
          Object.entries(loadErrors).map(([env, reason]) =>
            // Left out on purpose, not a failed read (user decision C3).
            reason === "PROD_ACCESS_FORBIDDEN" ? (
              <Banner key={env} tone="warn">
                prod 권한이 없어 dev TC만 보여 줍니다.
              </Banner>
            ) : (
              <Banner key={env} tone="error">
                {env} 목록을 불러오지 못했습니다 — 지금 보이는 목록에 {env} TC 가 빠져 있습니다. ({reason})
              </Banner>
            ),
          )}

        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            commitQ();
          }}
        >
          <Input
            aria-label="Query 검색"
            className="w-64"
            placeholder="Query 또는 TC 번호 검색"
            value={qDraft}
            onChange={(event) => setQDraft(event.target.value)}
            // Explicit rather than relying on the form's implicit submission:
            // that only fires under conditions the markup has to keep meeting,
            // and this is the screen's primary control.
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commitQ();
              }
            }}
          />
          {/* `items` is what makes the collapsed trigger read 은퇴만 rather
              than `retired` — without it Base UI prints the raw value. */}
          <Select items={STATE_FILTER_OPTIONS} value={search.state} onValueChange={applyState}>
            <SelectTrigger className="min-w-36" aria-label="상태 필터">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATE_FILTER_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select items={SORT_OPTIONS} value={search.sort} onValueChange={applySort}>
            <SelectTrigger className="min-w-36" aria-label="정렬">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Not decoration: with a narrow filter this is the only place
              that says how many rows are actually on screen, and the
              dev/prod split is the one place a prod-source TC announces
              itself (C9). */}
          <span className="ml-auto whitespace-nowrap text-xs text-muted-foreground tabular-nums">
            {visible.length} / {items.length} TC · dev {envCounts.dev} · prod {envCounts.prod}
          </span>
          {/* A selection the filter hides is still sent, so it is still
              counted — dropping it would lose work, hiding it would surprise. */}
          {picked.length > 0 && (
            <>
              <span className="text-xs whitespace-nowrap tabular-nums">
                {picked.length} 개 선택 · {picked.length === 1 ? "단일" : "다중"} 로 진행
                {offList > 0 && ` (현재 목록에 없는 ${offList} 건 포함)`}
              </span>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPicked([])}>
                선택 해제
              </Button>
            </>
          )}
          <Button
            type="button"
            size="sm"
            disabled={picked.length === 0 || handing}
            onClick={() => void startTest()}
          >
            {handing && <Loader2 className="animate-spin" />}
            {testLabel(picked.length)}
          </Button>
        </form>

        {/* Two-tier filter area (§3). Domain is "what is this question
            about", feedback category is "what went wrong" — one row each,
            because mixing them makes neither readable. */}
        <div className="grid gap-1.5">
          <div className="flex items-start gap-2">
            <span className="w-28 shrink-0 pt-0.5 text-xs text-muted-foreground">도메인</span>
            {chipDoms.length === 0 ? (
              <span className="pt-0.5 text-xs text-muted-foreground">
                분류된 도메인이 없습니다.
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {/* 전체 is the cleared filter, not a sixth domain — it is
                    how you get back without unpicking chips one by one. */}
                <button
                  type="button"
                  aria-pressed={search.doms.length === 0}
                  onClick={() => updateSearch({ doms: [] })}
                  className={cn(
                    badgeVariants({
                      variant: search.doms.length === 0 ? "accent" : "default",
                      size: "compact",
                    }),
                    "cursor-pointer",
                  )}
                >
                  전체 {items.length}
                </button>
                {chipDoms.map(({ slug, count }) => {
                  const on = search.doms.includes(slug);
                  const proposed = isProposedDomain(slug);
                  return (
                    <button
                      key={slug}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleDom(slug)}
                      // A proposal is a domain the rubric has no criteria
                      // for: it is stored and filterable, and its Category
                      // group is skipped until somebody writes them.
                      title={
                        proposed
                          ? "AI 제안 — rubric 에 기준이 없어 Category 채점에서 빠집니다"
                          : slug === UNCLASSIFIED_DOMAIN
                            ? "도메인이 없는 TC — Category 채점에서 빠집니다"
                            : undefined
                      }
                      className={cn(
                        badgeVariants({
                          variant: on ? "accent" : "default",
                          size: "compact",
                        }),
                        "cursor-pointer",
                      )}
                    >
                      {domainLabel(slug)} {count}
                      {proposed && <span className="ml-1 opacity-70">승인 대기</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex items-start gap-2">
            <span className="w-28 shrink-0 pt-0.5 text-xs text-muted-foreground">피드백 항목</span>
            {chipCats.length === 0 ? (
              <span className="pt-0.5 text-xs text-muted-foreground">
                이 도메인에 기록된 피드백 항목이 없습니다.
              </span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {chipCats.map((slug) => {
                  const selected = search.cats.includes(slug);
                  const retired = isRetiredCategory(slug);
                  return (
                    <button
                      key={slug}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => toggleCat(slug)}
                      // Past rows still carry it; new 👎 cannot.
                      title={
                        retired
                          ? "새 👎 에는 없는 항목입니다 — 응답 시간은 rubric v2 Execution 이 실행 기록으로 잽니다"
                          : undefined
                      }
                      className={cn(
                        badgeVariants({ variant: selected ? "accent" : "default", size: "compact" }),
                        "cursor-pointer",
                        retired && !selected && "text-muted-foreground",
                      )}
                    >
                      {slug}
                      {retired && <span className="text-[10px] text-muted-foreground">은퇴</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <Card className="overflow-hidden">
          {loading ? (
            <div className="grid gap-2 p-4">
              {Array.from({ length: 6 }, (_, i) => (
                <Skeleton key={i} className="h-9" />
              ))}
            </div>
          ) : items.length === 0 && error ? (
            <div className="grid gap-3 p-8 text-center">
              <p>목록을 불러오지 못했습니다.</p>
              <div className="flex justify-center">
                <Button variant="outline" onClick={() => void loadFirst()}>
                  다시 시도
                </Button>
              </div>
            </div>
          ) : items.length === 0 ? (
            filtersActive ? (
              <EmptyState
                title="조건에 맞는 TC 가 없습니다"
                body="필터를 줄여 보세요."
              />
            ) : (
              <EmptyState
                title="아직 TC 가 없습니다"
                body="👎 가 동의된 답변에 들어오면 자동으로 TC 로 승격되어 여기에 쌓입니다. 눌러야 할 것은 없습니다."
              />
            )
          ) : visible.length === 0 ? (
            <EmptyState
              title="조건에 맞는 TC 가 없습니다"
              body="필터를 줄여 보세요."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8">
                    <PickAll on={pickedOnScreen} total={pickable.length} onChange={setPickable} />
                  </TableHead>
                  <TableHead className="w-24">TC</TableHead>
                  <TableHead>Query</TableHead>
                  {/* The domain column keeps its slot in v1 — BE-DOMAIN fills
                      it with a value, the layout does not change. */}
                  <TableHead className="w-24">도메인</TableHead>
                  <TableHead className="w-56">피드백 항목</TableHead>
                  <TableHead className="w-16">Env</TableHead>
                  <TableHead className="w-16">상태</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((item) => (
                  <TestCaseRow
                    key={item.test_case_id}
                    item={item}
                    open={search.tc === item.test_case_id}
                    picked={pickedSet.has(item.test_case_id)}
                    onToggle={() => toggleDrawer(item.test_case_id)}
                    onPick={() => togglePick(item.test_case_id)}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {nextCursor && (
          <div className="flex justify-center">
            <Button variant="outline" onClick={() => void loadMore()} disabled={loadingMore}>
              {loadingMore && <Loader2 className="animate-spin" />}
              더 불러오기
            </Button>
          </div>
        )}
      </div>

      {search.tc && (
        <TestCaseDrawer
          openId={search.tc}
          source={source}
          onClose={() => updateSearch({ tc: null }, { replace: true })}
          onOpenOriginal={openOriginal}
          onOpenResult={openResult}
          onUpdated={adoptUpdated}
          originCandidates={originCandidates}
          loopApi={api}
        />
      )}
    </div>
  );
}

function testLabel(count: number) {
  if (count === 1) return "이 TC 로 Test하기 (단일)";
  if (count > 1) return `선택한 ${count} 개로 Test하기 (다중)`;
  return "선택한 TC 로 Test하기";
}

/** The header box: every listed, active row. A partial pick is indeterminate. */
function PickAll({
  on,
  total,
  onChange,
}: {
  on: number;
  total: number;
  onChange: (on: boolean) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = on > 0 && on < total;
  }, [on, total]);
  return (
    <input
      ref={ref}
      type="checkbox"
      aria-label="보이는 TC 전체 선택"
      checked={total > 0 && on === total}
      disabled={total === 0}
      onChange={(event) => onChange(event.target.checked)}
      className="accent-primary"
    />
  );
}

function TestCaseRow({
  item,
  open,
  picked,
  onToggle,
  onPick,
}: {
  item: TestCaseListItem;
  open: boolean;
  picked: boolean;
  onToggle: () => void;
  onPick: () => void;
}) {
  const batchGate = testCaseGate(item, "add_to_batch");
  return (
    <TableRow
      aria-current={open || undefined}
      onClick={onToggle}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onToggle();
        }
      }}
      tabIndex={0}
      className={cn(
        "cursor-pointer",
        // The open row's marker is an inset edge now that the first cell
        // holds the checkbox.
        open && "bg-accent-glow shadow-[inset_3px_0_0_var(--color-primary)] hover:bg-accent-glow",
        !item.is_active && "opacity-55",
      )}
    >
      {/* Picking is not opening: the label swallows the click and the key,
          so ticking a box never toggles the drawer. */}
      <TableCell className="w-8">
        <label
          className="flex items-center"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            aria-label={`${item.test_case_id} 선택`}
            checked={picked}
            // Retired TCs are out of every future batch (BE §13.5); beyond
            // that, whether a TC may go on a Batch page is the server's call
            // (or the legacy dev-only rule when it does not say).
            disabled={!item.is_active || !batchGate.allowed}
            title={!item.is_active && !isDropped(item) ? "은퇴한 TC 는 실행 대상이 아닙니다" : (batchGate.reason ?? undefined)}
            onChange={onPick}
            className="accent-primary"
          />
        </label>
      </TableCell>
      {/* Same value as the drawer's identifier block, so the same face —
          a TC id that changes typeface when you open the row reads as two
          different things. */}
      <TableCell className="w-24 text-xs">{item.test_case_id}</TableCell>
      <TableCell className="max-w-0 truncate">
        <span className="font-medium">{item.trigger_preview || item.title || "—"}</span>
        {/* Later 👎 on the same answer: read in the drawer, they change nothing. */}
        {(item.additional_feedback_count ?? 0) > 0 && (
          <Badge
            size="compact"
            className="ml-1.5 align-middle"
            title={`같은 답에 TC를 만든 👎 말고 ${item.additional_feedback_count}건이 더 달렸습니다. TC는 바뀌지 않습니다.`}
          >
            추가 👎 {item.additional_feedback_count}
          </Badge>
        )}
      </TableCell>
      {/* The domain the TC is judged against. A proposal is marked, because
          "classified" and "classified and scored" are different states. */}
      <TableCell className="w-24">
        {item.domain ? (
          <span
            className={cn("text-xs", isProposedDomain(item.domain) && "text-muted-foreground")}
            title={
              isProposedDomain(item.domain)
                ? `${item.domain} — AI 제안, rubric 에 기준이 없습니다`
                : item.domain
            }
          >
            {domainLabel(item.domain)}
            {isProposedDomain(item.domain) && " ·"}
          </span>
        ) : (
          <span className="text-muted-foreground" title="미분류 — Category 채점에서 빠집니다">
            —
          </span>
        )}
      </TableCell>
      <TableCell className="w-56">
        {item.categories.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {/* 1~3 badges; a truncated slug is unreadable, so the rest
                collapse to a count instead of ellipsizing a word. */}
            {item.categories.slice(0, 3).map((cat) => (
              <Badge key={cat} size="compact">
                {cat}
              </Badge>
            ))}
            {item.categories.length > 3 && (
              <Badge size="compact">+{item.categories.length - 3}</Badge>
            )}
          </span>
        )}
      </TableCell>
      <TableCell className="w-16">
        <span className="flex flex-wrap gap-1">
          <Badge size="compact">{item.source_environment}</Badge>
          {/* Unread, not deleted: the drawer says why and what still works. */}
          {isSourceUnavailable(item) && (
            <Badge size="compact" title={sourceUnavailableText(item.source_load_error)}>
              {SOURCE_UNAVAILABLE_LABEL}
            </Badge>
          )}
          {/* Read and gone — informational only; TCs are kept permanently. */}
          {isSourceDeleted(item) && (
            <Badge size="compact" title={SOURCE_DELETED_TEXT}>
              {SOURCE_DELETED_LABEL}
            </Badge>
          )}
        </span>
      </TableCell>
      <TableCell className="w-16">
        {/* Dropped reads apart from retired: a run dropped it, nobody chose
            to. The two are independent, so the manual state stays shown. */}
        <span className="flex flex-wrap gap-1">
          {isDropped(item) && (
            <Badge size="compact" className="text-destructive" title={droppedReasonText(item.drop_reason_code)}>
              {DROPPED_LABEL}
            </Badge>
          )}
          <Badge size="compact" variant={item.is_active ? "accent" : "default"}>
            {item.is_active ? "활성" : "은퇴"}
          </Badge>
        </span>
      </TableCell>
    </TableRow>
  );
}

/**
 * The row's domain as a filter value. An absent field and a null column are
 * the same thing to this screen — "no domain, Category drops out" — and both
 * have to answer to one chip.
 */
function domainOf(item: TestCaseListItem) {
  return item.domain ?? UNCLASSIFIED_DOMAIN;
}

function filterTestCases(items: readonly TestCaseListItem[], search: AllTestCasesSearch) {
  return items.filter((item) => {
    if (search.state === "active" && !item.is_active) return false;
    if (search.state === "retired" && item.is_active) return false;
    // OR across the picked domains, with the null column as a value of its
    // own — most of the table is unclassified and has to stay reachable.
    if (search.doms.length > 0 && !search.doms.includes(domainOf(item))) return false;
    // The snapshot column, not the union: that is what the GIN index and the
    // spec's filter contract both point at.
    if (search.cats.length > 0 && !item.categories.some((cat) => search.cats.includes(cat))) {
      return false;
    }
    if (search.q) {
      const needle = search.q.toLowerCase();
      const haystacks = [item.test_case_id, item.trigger_preview ?? ""];
      if (!haystacks.some((hay) => hay.toLowerCase().includes(needle))) return false;
    }
    return true;
  });
}

function sortTestCases(items: readonly TestCaseListItem[], sort: AllTestCasesSort) {
  const copy = [...items];
  const byPromotedDesc = (a: TestCaseListItem, b: TestCaseListItem) =>
    b.promoted_at.localeCompare(a.promoted_at) || a.test_case_id.localeCompare(b.test_case_id);
  switch (sort) {
    case "tc":
      copy.sort((a, b) => a.test_case_id.localeCompare(b.test_case_id, undefined, { numeric: true }));
      break;
    case "feedback":
      copy.sort((a, b) => compareFeedbackCountDesc(a, b) || byPromotedDesc(a, b));
      break;
    default:
      copy.sort(byPromotedDesc);
  }
  return copy;
}
